import styled from 'styled-components';

import classNames from "classnames";
import {useToast} from "@chakra-ui/react";
import React, {useState} from "react";
import {Award as LucideAward, Check, Eraser, Hand, Handshake, Pin} from "lucide-react";
import {useRelativeTime, useStoreCommunity, useStoreProfile} from '../store';
import {themecolors} from "../utils/themecolors";
import useFeedback from "../utils/use_feedback";
import {useCommunitySubscribe} from "../api/communities";
import useProfilePicture from "../utils/profile_picture";
import NavLink from "./nav_link";
import Skeleton from './skeleton';
import RoleIndicator from "./role_indicator";
import CommunityLogo from "./community_logo";
import {BareButton} from "./button";
import Sheet from "./bottom-sheet/sheet";
import PostActionsSheet from "./bottom-sheet/post_actions";
import PostExpand from "./post_expand";
import SeparatorBullet from "./separator_bullet";
import ProfilePicture from "./profile_picture";

const Infos = styled.div``;
const Title = styled.div``;
const Details = styled.div``;
const Text = styled.div``;
const Preview = styled.div``;
const TextAndPreview = styled.div``;
const Stickied = styled.div``;
const Logo = styled.div``;
const Options = styled.div``;
const NewUser = styled.span``;
const Approved = styled.span``;
const Award = styled.div``;
const CommunityFeed = styled.div``;
const SubscribeButton = styled.div``;
const Edited = styled.div``;
const Avatar = styled.div``;
const CommunityAuthor = styled.div``;
const Dots = styled.div``;
const PreviewOverlap = styled.div``;

const CondensedPost = styled(({className, post, children, queueContext, sort, isCommunityFeed, inFeed, isTrending = false, expandState, setExpandState, mainFeedIndex = false}) => {
    const {theme} = useStoreProfile();
    const community = useStoreCommunity(post?.community);
    const ago = useRelativeTime((sort && sort === 'active') ? post?.last_comment_created: post?.created);
    const reported = post?.moderation && post.moderation.is_reported;
    const removed = post?.moderation && post.moderation.removed_at !== 0;

    const {feedbackCh} = useFeedback();
    const subscribe = useCommunitySubscribe(feedbackCh);
    const toast = useToast();
    const handleSubscribe = async (event) => {
        event.preventDefault();
        try {
            await subscribe([community.name]);
        } catch {
            toast({
                title: `Failed to subscribe.`,
                status: 'error',
                isClosable: true,
            });
        }
    }

    const expanded = expandState?.includes(post?.id);
    const handleExpand = async (event) => {

        event.stopPropagation();
        event.preventDefault();

        if (!expandState) return;

        const isIdExpanded = expanded;

        const updatedExpandState = isIdExpanded
            ? expandState.filter((expandedId) => expandedId !== post.id)
            : [...expandState, post.id];

        setExpandState(updatedExpandState);

    }

    const [actionsOpen, setActionsOpen] = useState(false);

    return (
        <div className={classNames(className, {removed, reported: reported && !queueContext})}>
            <Infos style={{height: !isCommunityFeed ? 40 : 30, marginBottom: !isCommunityFeed ? 8 : 2}}>
                {!isCommunityFeed &&
                <Logo>
                    {post
                        ? <CommunityLogo name={post?.community}/>
                        : <Skeleton circle height={32} width={32}/>}
                </Logo>
                }
                <Details>
                    {post
                        ? <>
                            {!isCommunityFeed &&
                            <CommunityFeed>
                                <NavLink href={`/c/${post.community}`}>{post.community}</NavLink>
                            </CommunityFeed>
                            }
                            {!inFeed || (inFeed && isCommunityFeed) &&
                                <Avatar>
                                    <ProfilePicture size={20} fontSize={11} username={post.author} profilePicture={useProfilePicture(post.author, post.profile_picture, theme)} />
                                </Avatar>
                            }
                            {isCommunityFeed ?
                                <CommunityAuthor>
                                    <NavLink href={`/u/${post.author}`}>{post.author}</NavLink>
                                </CommunityAuthor>
                                :
                                <NavLink href={`/u/${post.author}`}>{post.author}</NavLink>
                            }
                            <RoleIndicator admin={post.is_admin} mod={post.is_moderator} proTier={post.pro_tier} />
                            <SeparatorBullet />
                            {ago} ago
                            {post?.awards > 0 &&
                                <Award>
                                    <LucideAward size={14} color={"#dcba00"}/>
                                </Award>
                            }
                            {post?.is_edited && (
                                <Edited>
                                    <Eraser size={14} />
                                </Edited>
                            )}
                            {post?.is_new_user && (
                                <NewUser>
                                    <Hand size={14} />
                                </NewUser>
                            )}
                            {post?.moderation && post.moderation?.approved_at !== 0 && <Approved><Check size={16} /></Approved>}
                            {post?.is_stickied &&
                                <Stickied>
                                    <Pin size={14} color={"#ffbe00"} fill={"#ffbe00"} />
                                </Stickied>
                            }
                        </>
                        : <Skeleton height={12} count={2} />}
                </Details>
                <Options>
                    {!isCommunityFeed && inFeed && community.created && !community?.is_subscribed &&
                        <BareButton onClick={handleSubscribe}>
                            <SubscribeButton>
                                Join
                            </SubscribeButton>
                        </BareButton>
                    }
                </Options>
            </Infos>
            <TextAndPreview>
                <Text>
                    <Title>
                        {post ? post.title : <Skeleton height={10} count={2} />}
                    </Title>
                </Text>
                    <Preview style={{filter: post?.is_nsfw && "blur(4px)"}} onClick={handleExpand}>
                        {post.preview &&
                            <PreviewOverlap>
                                {post.is_twitter && '𝕏'}
                                {post.domain && !post.is_twitter && !post.is_image && post.domain.replace(/^www\./, "")}
                            </PreviewOverlap>
                        }
                        {post.preview && <img src={post.preview} />}
                    </Preview>
            </TextAndPreview>
            {expanded &&
                <PostExpand post={post} inFeed={inFeed} expand />
            }
            {children}
            {actionsOpen && <Sheet onClose={() => setActionsOpen(false) } padding={"menu"}><PostActionsSheet isTrending={isTrending} postId={post?.id} onClose={() => setActionsOpen(false) } /></Sheet>}
        </div>
    );
})`
  background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_1 : theme.white_0};
  padding-top: ${({mainFeedIndex}) => mainFeedIndex === 0 ? 10 : 10}px;
  padding-bottom: 2px;

  ${Avatar} {
    width: 20px;
    margin-right: 8px;
  }
  ${Avatar} img {
    border-radius: 50%;
  }
  & img {
    max-width: 100%;
    display: flex;
    margin: 0 auto;
  }
  
  ${Text} {
    flex-grow: 1;
  }

  ${Infos} {
    height: 40px;
    padding: 0 16px;
    font-size: ${({isCommunityFeed}) => isCommunityFeed ? 12 : 13}px;
    min-height: 30px;
    display: flex;
    align-items: center;
  }

  ${Title} {
    padding: 0px 16px 2px 16px;
    font-size: 14px;
    color: ${({theme}) => theme.post.primary};
    word-break: break-word;
    font-weight: 600;
  }
  ${Title} svg {
    font-size: 10px;
    margin-left: 4px;
    color: ${({theme}) => theme.post.primary};
  }

  ${Details} {
    flex-grow: 1;
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    white-space: break-spaces;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_low_impact_text};
  }

  ${Details} ${Skeleton} {
    flex-basis: 160px;
  }

  ${CommunityFeed} a {
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
    font-weight: 600;
  }
  ${CommunityFeed} {
    width: 100%;
    margin-bottom: -4px;
  }
  
  ${Stickied} {
    margin-left: 6px;
    margin-right: 8px;
  }
  
  ${TextAndPreview} {
    display: flex;
  }

  ${Logo} {
    flex: 0 0 32px;
    height: 32px;
    margin-right: 10px;
  }

  ${Logo} img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  ${Options} {
    margin-left: auto;
    display: flex;
    align-items: center;
  }

  ${NewUser}, ${Edited} {
    display: flex;
    font-size: 12px;
    margin-left: 8px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_low_impact_text};
  }

  ${Approved} {
    color: ${() => themecolors.green};
    margin-left: 8px;
    font-size: 13px;
  }

  ${Award} {
    display: flex;
    margin-left: 8px;
  }

  &.removed {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.pink_2 : theme.pink_1};
  }
  &.reported {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.yellow_2 : theme.yellow_1};
  }

  ${Preview} {
    margin-top: -26px;
    width: 82px;
    height: 62px;
    margin-left: auto;
    margin-right: 10px;
    overflow: hidden;
    text-align: right;
    flex-shrink: 0;
    border-radius: 8px;
    position: relative;
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_5 : '#e5e5e5'};
    border: 1px solid ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_5 : themecolors.light_border};
  }
  ${Preview} img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
  ${PreviewOverlap} {
    position: absolute;
    font-size: 11px;
    bottom: 4px;
    left: 4px;
    border-radius: 4px;
    background-color: rgba(0, 0, 0, .8);
    white-space: nowrap;
    max-width: calc(100% - 8px);
    padding: 0 6px;
    overflow: hidden;
    color: ${() => themecolors.dark_high_impact_text};
    z-index: 2;
  }

  ${SubscribeButton} {
    font-size: 14px;
    font-weight: 600;
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_4 : '#000000'};
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : '#e9e9e9'};
    padding: 4px 12px;
    border-radius: 48px;
    margin-right: 8px;
  }

  ${CommunityAuthor} {
    font-weight: 600;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
  ${Dots} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_medium_impact_text};
  }
)
    
`;

export default CondensedPost;
